<template>
  <el-input
    :class="isPlain ? '' : `w100 has-suffix${isSmallSuffix ? ' small-suffix' : ''}`"
    :value="display"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="isPlain ? undefined : size"
    @input="handleInput"
    @focus="handleFocus"
    @blur="handleBlur"
    @change="handleChange"
  >
    <template v-if="isShowSuffix" slot="append">{{ suffixName }}</template>
  </el-input>
</template>

<script>
import intTypes from "@/enums/intTypes";

/**
 * 数字输入框（受控）
 *
 * 2026-09 重写，替代原先 keyup + keyCode + lastValue 的实现：
 * 1. 唯一数据源是输入框展示字符串 display，全部走字符串清洗，
 *    不再出现「输入 0 后再打小数点被吞」的问题（旧实现依赖 keyCode 判断特殊键，且用 lastValue 回滚）。
 * 2. 清洗规则：只保留数字与小数点；precision 为 0 时不允许小数点；小数位截断到 precision；
 *    仅当 min < 0 时允许开头一个负号。
 * 3. 钳制时机：输入过程中只钳「上限」（超过 max 立即回退，用户实时可见，避免失焦后被静默改成最大值）；
 *    下限与精度收敛放到失焦时统一处理——下限若实时钳，会让「从 0 开始输入 0.5」直接被打断。
 * 4. 对外 value 统一为「数字」或空字符串 ''，不再吐出 '0.' 之类的中间态字符串。
 *
 * 样式变体：默认是表单宽版（w100 + has-suffix + size）；
 * isPlain=true 时去掉这些（用于表格内、行内小输入，原 VInputNumberCenter 的场景）。
 */
export default {
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    precision: {
      type: Number,
      default: 0,
    },
    type: {
      type: String,
      default: 'unsignedInt',
    },
    isShowSuffix: {
      type: Boolean,
      default: false,
    },
    isSmallSuffix: {
      type: Boolean,
      default: false,
    },
    suffixName: {
      type: String,
      default: '元',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    max: {
      type: [String, Number],
      default: undefined,
    },
    min: {
      type: [String, Number],
      default: undefined,
    },
    size: {
      type: String,
      default: 'medium',
    },
    // 去掉宽版样式与固定尺寸（表格内/行内场景）
    isPlain: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    display: '',
    isFocused: false,
    minBackup: 0,
    maxBackup: 0,
  }),
  computed: {
    isNegativeAllowed() {
      return this.minBackup < 0;
    },
  },
  watch: {
    value(value) {
      const next = this.isBlank(value) ? '' : String(value);
      // 聚焦期间以用户输入为准；只有外部显式清空，或数值确实变了，才回写展示
      if (this.isFocused) {
        if (next === '') {
          this.display = '';
        } else if (this.isIncomplete(this.display) || +next !== +this.display) {
          this.display = next;
        }
        return;
      }
      this.display = next;
    },
    min(min) {
      this.minBackup = min === undefined || min === '' ? this.defaultMin() : +min;
    },
    max(max) {
      this.maxBackup = max === undefined || max === '' ? this.defaultMax() : +max;
    },
  },
  beforeMount() {
    this.minBackup = this.min === undefined || this.min === '' ? this.defaultMin() : +this.min;
    this.maxBackup = this.max === undefined || this.max === '' ? this.defaultMax() : +this.max;
    this.display = this.isBlank(this.value) ? '' : String(this.value);
  },
  methods: {
    defaultMin() {
      const conf = intTypes[this.type] || intTypes.unsignedInt;
      return this.precision ? conf.min / Math.pow(10, this.precision) : conf.min;
    },
    defaultMax() {
      const conf = intTypes[this.type] || intTypes.unsignedInt;
      return this.precision ? conf.max / Math.pow(10, this.precision) : conf.max;
    },
    isBlank(value) {
      return value === '' || value === null || value === undefined;
    },
    // 空串、只有负号、只有小数点都算「尚未输入完成」
    isIncomplete(str) {
      return str === '' || str === '-' || str === '.' || str === '-.';
    },
    sanitize(raw) {
      let str = String(raw === null || raw === undefined ? '' : raw);
      const negative = this.isNegativeAllowed && str.charAt(0) === '-';
      // 去掉数字与小数点之外的所有字符（逗号、空格、字母等）
      str = str.replace(/[^\d.]/g, '');
      const dotIndex = str.indexOf('.');
      if (dotIndex !== -1) {
        const intPart = str.slice(0, dotIndex);
        const decPart = str.slice(dotIndex + 1).replace(/\./g, '');
        str = this.precision > 0 ? `${intPart}.${decPart.slice(0, this.precision)}` : intPart;
      }
      return negative && str !== '' ? `-${str}` : str;
    },
    emitValue(str) {
      this.$emit('input', this.isIncomplete(str) ? '' : +str);
    },
    handleInput(raw) {
      let next = this.sanitize(raw);
      // 输入时只钳上限：超过 max 立即回退，用户实时可见，避免失焦后被静默修正
      if (!this.isIncomplete(next) && +next > this.maxBackup) next = String(this.maxBackup);
      this.display = next;
      this.emitValue(next);
    },
    handleFocus() {
      this.isFocused = true;
    },
    handleBlur(event) {
      this.isFocused = false;
      const raw = event && event.target ? event.target.value : this.display;
      let next = this.sanitize(raw);
      if (next.charAt(0) === '.') {
        next = `0${next}`;
      } else if (next.charAt(0) === '-' && next.charAt(1) === '.') {
        next = `-0${next.slice(1)}`;
      }
      if (this.isIncomplete(next)) {
        this.display = '';
        this.$emit('input', '');
        this.$emit('blur', '');
        return;
      }

      // 失焦时统一钳到 [min, max] 并按精度收敛
      let num = +next;
      if (num > this.maxBackup) num = this.maxBackup;
      if (num < this.minBackup) num = this.minBackup;
      num = this.roundByPrecision(num);
      this.display = String(num);
      this.$emit('input', num);
      this.$emit('blur', num);
    },
    handleChange() {
      this.$emit('change', this.isIncomplete(this.display) ? '' : +this.display);
    },
    roundByPrecision(num) {
      if (!this.precision) return Math.round(num);
      const factor = Math.pow(10, this.precision);
      return Math.round(num * factor) / factor;
    },
  },
};
</script>

<style src="./index.css" scoped/>
